import React, { useCallback, useEffect, useState } from 'react'
import { Search } from 'lucide-react'
import { useI18n } from '../../i18n'
import { homeApi } from '../../services/api'
import { Button } from '../ui/Button'
import LanguageSwitcher from '../LanguageSwitcher'
import { getActiveOrgId } from '../../utils/orgUtils'
import styles from '@/styles/components/layout/HomeDashboardHeaderActions.module.scss'

/**
 * Last scan + Run Scan for the global page header on the home route only.
 * Fetches security health independently so it can live in PageLayout (outside Home body).
 */
export function HomeDashboardHeaderActions() {
  const { t } = useI18n()
  const [lastScanDateStr, setLastScanDateStr] = useState<string>('—')

  const refreshLastScan = useCallback(async () => {
    try {
      const res = await homeApi.getSecurityHealth()
      const raw = res?.lastScan
      setLastScanDateStr(
        raw ? new Date(raw as string | number).toLocaleString() : '—',
      )
    } catch {
      setLastScanDateStr('—')
    }
  }, [])

  useEffect(() => {
    refreshLastScan()
  }, [refreshLastScan])

  useEffect(() => {
    const onRefresh = () => {
      refreshLastScan()
    }
    window.addEventListener('compliance-updated', onRefresh)
    return () => window.removeEventListener('compliance-updated', onRefresh)
  }, [refreshLastScan])

  const startFullScan = async () => {
    try {
      const orgId = getActiveOrgId()
      const payload = { orgId, targets: ['ALL'], scannerType: 'openvas' }
      const res = await fetch('/api/scan-provider/scans/start', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error('Failed to request scan')
      alert('✅ Scan request created.')
      refreshLastScan()
    } catch (e: unknown) {
      alert((e as Error)?.message || 'Failed to create scan request')
    }
  }

  const lastScanLabel = t('scan.last_scan', 'Last scan')
  const runScanLabel = t('scan.start_scan', 'Run Scan')

  return (
    <div className={styles.root}>
      <LanguageSwitcher appearance="outline" />
      <span className={styles.lastScan}>
        {lastScanLabel}:{' '}
        <span className={styles.lastScanValue}>{lastScanDateStr}</span>
      </span>
      <Button
        type="button"
        size="small"
        color="outline"
        onClick={startFullScan}
        icon={<Search className={styles.icon} aria-hidden />}
      >
        {runScanLabel}
      </Button>
    </div>
  )
}
